import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import SearchableSelect from './SearchableSelect';
import Pagination from './Pagination';
import './css/BaobabDesignSystem.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const FleetManagement = ({ transporteurId, transporteurName, onClose }) => {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [truckTypes, setTruckTypes] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [formData, setFormData] = useState({
    immatriculation_1: '',
    immatriculation_2: '',
    type_camion: '',
    capacite_max_tonnes: '',
    usage: 'transport_marchandise',
    carte_grise_valide: false,
    assurance_valide: false,
    date_expiration_carte_grise: '',
    date_expiration_assurance: '',
    gps_installe: false,
    gps_actif: false,
    notes: ''
  });

  const usageOptions = [
    { value: 'transport_marchandise', label: '🚚 Transport de marchandise' },
    { value: 'location', label: '🏢 Location' },
    { value: 'reserve', label: '🔧 Réserve' }
  ];

  useEffect(() => {
    fetchFleetData();
    fetchTruckTypes();
  }, [transporteurId]);

  // Charger les types de camions depuis l'API
  const fetchTruckTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/truck-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Formater pour SearchableSelect
        const formatted = (data.truck_types || []).map(truck => ({
          value: truck.type,
          label: truck.type,
          type: truck.type
        }));
        setTruckTypes(formatted);
      }
    } catch (error) {
      console.error('Erreur chargement types camions:', error);
    }
  };

  const fetchFleetData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transporteurs/${transporteurId}/fleet`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVehicles(data.vehicles);
        setStatistics(data.statistics);
      } else {
        console.error('Erreur lors de la récupération de la flotte');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    try {
      const vehicleData = {
        ...formData,
        transporteur_id: transporteurId,
        capacite_max_tonnes: parseFloat(formData.capacite_max_tonnes),
        date_expiration_carte_grise: formData.date_expiration_carte_grise || null,
        date_expiration_assurance: formData.date_expiration_assurance || null
      };

      const response = await fetch(`${API_BASE_URL}/api/fleet-vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(vehicleData)
      });

      if (response.ok) {
        alert('Véhicule ajouté avec succès !');
        setShowCreateModal(false);
        resetForm();
        fetchFleetData();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail || 'Impossible de créer le véhicule'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création du véhicule');
    }
  };

  const handleEditVehicle = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...formData,
        capacite_max_tonnes: parseFloat(formData.capacite_max_tonnes),
        date_expiration_carte_grise: formData.date_expiration_carte_grise || null,
        date_expiration_assurance: formData.date_expiration_assurance || null
      };

      const response = await fetch(`${API_BASE_URL}/api/fleet-vehicles/${selectedVehicle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('Véhicule modifié avec succès !');
        setShowEditModal(false);
        setSelectedVehicle(null);
        resetForm();
        fetchFleetData();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail || 'Impossible de modifier le véhicule'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification du véhicule');
    }
  };

  const handleDeleteVehicle = async (vehicle) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le véhicule ${vehicle.immatriculation_1} ?`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/fleet-vehicles/${vehicle.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          alert('Véhicule supprimé avec succès !');
          fetchFleetData();
        } else {
          alert('Erreur lors de la suppression du véhicule');
        }
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression du véhicule');
      }
    }
  };

  const openEditModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      immatriculation_1: vehicle.immatriculation_1 || '',
      immatriculation_2: vehicle.immatriculation_2 || '',
      type_camion: vehicle.type_camion || '',
      capacite_max_tonnes: vehicle.capacite_max_tonnes?.toString() || '',
      usage: vehicle.usage || 'transport_marchandise',
      carte_grise_valide: vehicle.carte_grise_valide || false,
      assurance_valide: vehicle.assurance_valide || false,
      date_expiration_carte_grise: vehicle.date_expiration_carte_grise ? vehicle.date_expiration_carte_grise.split('T')[0] : '',
      date_expiration_assurance: vehicle.date_expiration_assurance ? vehicle.date_expiration_assurance.split('T')[0] : '',
      gps_installe: vehicle.gps_installe || false,
      gps_actif: vehicle.gps_actif || false,
      notes: vehicle.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      immatriculation_1: '',
      immatriculation_2: '',
      type_camion: '',
      capacite_max_tonnes: '',
      usage: 'transport_marchandise',
      carte_grise_valide: false,
      assurance_valide: false,
      date_expiration_carte_grise: '',
      date_expiration_assurance: '',
      gps_installe: false,
      gps_actif: false,
      notes: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getExpirationStatus = (dateString) => {
    if (!dateString) return 'warning';
    const expDate = new Date(dateString);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    
    if (expDate < now) return 'expired';
    if (expDate < thirtyDaysFromNow) return 'expiring';
    return 'valid';
  };

  const getStatusBadge = (status, text) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'expired':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'expiring':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'valid':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'warning':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="baobab-app">
        <div className="baobab-loading">
          <div className="text-center">
            <div className="baobab-spinner mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement de la flotte</h3>
            <p className="baobab-loading-text">Préparation des données véhicules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="baobab-app">
      {/* Header */}
      <div className="baobab-header">
        <div className="baobab-header-content">
          <div className="baobab-header-main">
            <div className="baobab-title-section">
              <div className="baobab-fade-in">
                <h1 className="baobab-title">Flotte - {transporteurName}</h1>
              </div>
            </div>
            <div className="baobab-actions flex items-center gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter véhicule
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Fermer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Total Véhicules</h3>
              <p className="text-2xl font-bold text-green-600">{statistics.total_vehicles}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Actifs</h3>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_vehicles}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">GPS Équipés</h3>
              <p className="text-2xl font-bold text-purple-600">{statistics.gps_enabled}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Docs à renouveler</h3>
              <p className="text-2xl font-bold text-yellow-600">{statistics.documents_expiring_soon}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Types différents</h3>
              <p className="text-2xl font-bold text-gray-600">{Object.keys(statistics.by_type || {}).length}</p>
            </div>
          </div>
        )}

        {/* Vehicles List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Immatriculation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type / Capacité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GPS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{vehicle.immatriculation_1}</div>
                      {vehicle.immatriculation_2 && (
                        <div className="text-sm text-gray-500">{vehicle.immatriculation_2}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.type_camion}</div>
                      <div className="text-sm text-gray-500">{vehicle.capacite_max_tonnes} tonnes</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge('valid', vehicle.usage)}>
                        {usageOptions.find(opt => opt.value === vehicle.usage)?.label || vehicle.usage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={getStatusBadge(getExpirationStatus(vehicle.date_expiration_carte_grise), 'Carte grise')}>
                          CG: {formatDate(vehicle.date_expiration_carte_grise)}
                        </span>
                        <br />
                        <span className={getStatusBadge(getExpirationStatus(vehicle.date_expiration_assurance), 'Assurance')}>
                          Ass: {formatDate(vehicle.date_expiration_assurance)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className={`flex items-center ${vehicle.gps_installe ? 'text-green-600' : 'text-gray-400'}`}>
                          📡 {vehicle.gps_installe ? 'Installé' : 'Non'}
                        </div>
                        {vehicle.gps_installe && (
                          <div className={`flex items-center text-xs ${vehicle.gps_actif ? 'text-green-600' : 'text-orange-600'}`}>
                            🟢 {vehicle.gps_actif ? 'Actif' : 'Inactif'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(vehicle)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          ✏️ Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteVehicle(vehicle)}
                          className="text-red-600 hover:text-red-900"
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {vehicles.length === 0 && (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🚛</div>
              <p className="text-gray-500 text-lg">Aucun véhicule dans la flotte</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Ajouter le premier véhicule
              </button>
            </div>
          )}

          {/* Pagination */}
          {vehicles.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(vehicles.length / itemsPerPage)}
              totalItems={vehicles.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      </div>

      {/* Create Vehicle Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Ajouter un véhicule</h2>
              <button
                onClick={() => {setShowCreateModal(false); resetForm();}}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateVehicle} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Immatriculation principale *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.immatriculation_1}
                    onChange={(e) => setFormData({...formData, immatriculation_1: e.target.value.toUpperCase()})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: CI-1234-AB"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Immatriculation secondaire
                  </label>
                  <input
                    type="text"
                    value={formData.immatriculation_2}
                    onChange={(e) => setFormData({...formData, immatriculation_2: e.target.value.toUpperCase()})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: remorque"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de camion *
                </label>
                <SearchableSelect
                  options={truckTypes}
                  value={formData.type_camion}
                  onChange={(value) => setFormData({...formData, type_camion: value})}
                  placeholder="Rechercher un type de camion..."
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacité maximale (tonnes) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    value={formData.capacite_max_tonnes}
                    onChange={(e) => setFormData({...formData, capacite_max_tonnes: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usage</label>
                  <select
                    value={formData.usage}
                    onChange={(e) => setFormData({...formData, usage: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {usageOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date expiration carte grise
                  </label>
                  <input
                    type="date"
                    value={formData.date_expiration_carte_grise}
                    onChange={(e) => setFormData({...formData, date_expiration_carte_grise: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date expiration assurance
                  </label>
                  <input
                    type="date"
                    value={formData.date_expiration_assurance}
                    onChange={(e) => setFormData({...formData, date_expiration_assurance: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.carte_grise_valide}
                      onChange={(e) => setFormData({...formData, carte_grise_valide: e.target.checked})}
                      className="mr-2"
                    />
                    Carte grise valide
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.assurance_valide}
                      onChange={(e) => setFormData({...formData, assurance_valide: e.target.checked})}
                      className="mr-2"
                    />
                    Assurance valide
                  </label>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.gps_installe}
                      onChange={(e) => setFormData({...formData, gps_installe: e.target.checked})}
                      className="mr-2"
                    />
                    GPS installé
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.gps_actif}
                      onChange={(e) => setFormData({...formData, gps_actif: e.target.checked})}
                      className="mr-2"
                    />
                    GPS actif
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Informations complémentaires, état du véhicule, etc..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowCreateModal(false); resetForm();}}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Ajouter le véhicule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Vehicle Modal */}
      {showEditModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Modifier le véhicule</h2>
              <button
                onClick={() => {setShowEditModal(false); setSelectedVehicle(null); resetForm();}}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditVehicle} className="space-y-4">
              {/* Same form as create modal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Immatriculation principale *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.immatriculation_1}
                    onChange={(e) => setFormData({...formData, immatriculation_1: e.target.value.toUpperCase()})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: CI-1234-AB"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Immatriculation secondaire
                  </label>
                  <input
                    type="text"
                    value={formData.immatriculation_2}
                    onChange={(e) => setFormData({...formData, immatriculation_2: e.target.value.toUpperCase()})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: remorque"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de camion *
                </label>
                <SearchableSelect
                  options={truckTypes}
                  value={formData.type_camion}
                  onChange={(value) => setFormData({...formData, type_camion: value})}
                  placeholder="Rechercher un type de camion..."
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacité maximale (tonnes) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    value={formData.capacite_max_tonnes}
                    onChange={(e) => setFormData({...formData, capacite_max_tonnes: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usage</label>
                  <select
                    value={formData.usage}
                    onChange={(e) => setFormData({...formData, usage: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {usageOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date expiration carte grise
                  </label>
                  <input
                    type="date"
                    value={formData.date_expiration_carte_grise}
                    onChange={(e) => setFormData({...formData, date_expiration_carte_grise: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date expiration assurance
                  </label>
                  <input
                    type="date"
                    value={formData.date_expiration_assurance}
                    onChange={(e) => setFormData({...formData, date_expiration_assurance: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.carte_grise_valide}
                      onChange={(e) => setFormData({...formData, carte_grise_valide: e.target.checked})}
                      className="mr-2"
                    />
                    Carte grise valide
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.assurance_valide}
                      onChange={(e) => setFormData({...formData, assurance_valide: e.target.checked})}
                      className="mr-2"
                    />
                    Assurance valide
                  </label>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.gps_installe}
                      onChange={(e) => setFormData({...formData, gps_installe: e.target.checked})}
                      className="mr-2"
                    />
                    GPS installé
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.gps_actif}
                      onChange={(e) => setFormData({...formData, gps_actif: e.target.checked})}
                      className="mr-2"
                    />
                    GPS actif
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Informations complémentaires, état du véhicule, etc..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowEditModal(false); setSelectedVehicle(null); resetForm();}}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Sauvegarder les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetManagement;